/* eslint-disable react-hooks/exhaustive-deps */
import { useLiveQuery } from "dexie-react-hooks";
import { useEffect, useMemo, useState } from "react";
import { db } from "~/db/db";
import { semanaVigente, todasAsDatasDoMes } from "~services/GlobalServices";

const useRota = ({ filters = {}, skip = 0, take = 10, vendedorSelecionado } = {}) => {
  const [days, setDays] = useState([]);
  const [todasRotasHoje, setTodasRotasHoje] = useState([]);
  const [todasRotasAmanha, setTodasRotasAmanha] = useState([]);
  const [rotas, setRotas] = useState([]);
  const [total, setTotal] = useState(0);

  const filtrarPorVendedor = (result) =>
    typeof vendedorSelecionado === "number" ? result.filter((rota) => rota.usuarioId === vendedorSelecionado) : result;


  useLiveQuery(() => {
    const result = [];
    const dataAtual = new Date().toLocaleDateString("pt-BR");

    return db.rotas
      .where("dataLabel")
      .equals(dataAtual)
      .each((a) => result.push(a))
      .then(() => setTodasRotasHoje(filtrarPorVendedor(result)));
  }, [vendedorSelecionado]);

  useLiveQuery(() => {
    const result = [];
    const dataAmanha = new Date();
    dataAmanha.setDate(dataAmanha.getDate() + 1);

    return db.rotas
      .where("dataLabel")
      .equals(dataAmanha.toLocaleDateString("pt-BR"))
      .each((a) => result.push(a))
      .then(() => setTodasRotasAmanha(filtrarPorVendedor(result)));
  }, [vendedorSelecionado]);

  useLiveQuery(() => {
    const result = [];

    return db.rotas
      .where("dataLabel")
      .anyOf(days)
      .and((a) => {
        const passaNoFiltroTexto = filters.search
          ? [
              (a.codigoCliente ?? "").toLowerCase(),
              (a.nomeCliente ?? "").toLowerCase(),
              a.enderecoCliente.toLowerCase(),
            ].some((e) => e.includes(filters.search.toLowerCase()))
          : true;

        const passaNoFiltroVendedor = vendedorSelecionado != null ? a.usuarioId === vendedorSelecionado : true;

        return passaNoFiltroTexto && passaNoFiltroVendedor;
      })
      .each((a) => result.push(a))
      .then(() => {
        setTotal(result.length);
        setRotas(
          result
            .sort((a, b) => {
              const dateComparison = a.dataLabel.localeCompare(b.dataLabel);
              return dateComparison === 0 ? a.sequencia - b.sequencia : dateComparison;
            })
            .slice(0, skip + take)
        );
      });
  }, [days, skip, take, filters?.search, vendedorSelecionado]);

  const rotasCoordenadas = useMemo(() => {
    const dataAtual = new Date().toLocaleDateString("pt-BR");
    return todasRotasHoje
      .filter((rota) => {
        const lat = rota.latitude;
        const lng = rota.longitude;
        return (
          lat &&
          lng &&
          typeof lat === "string" &&
          typeof lng === "string" &&
          lat.trim() !== "" &&
          lng.trim() !== "" &&
          rota?.visita?.dataLabel !== dataAtual
        );
      })
      .map((rota) => ({
        latitude: parseFloat(rota.latitude.replace(/,/g, ".")),
        longitude: parseFloat(rota.longitude.replace(/,/g, ".")),
      }));
  }, [todasRotasHoje]);

  const rotasCoordenadasAmanha = useMemo(() => {
    const dataAmanha = new Date();
    dataAmanha.setDate(dataAmanha.getDate() + 1);
    const dataAmanhaFormatada = dataAmanha.toLocaleDateString("pt-BR");
    return todasRotasAmanha
      .filter((rota) => {
        const lat = rota.latitude;
        const lng = rota.longitude;
        return (
          lat &&
          lng &&
          typeof lat === "string" &&
          typeof lng === "string" &&
          lat.trim() !== "" &&
          lng.trim() !== "" &&
          rota?.visita?.dataLabel !== dataAmanhaFormatada
        );
      })
      .map((rota) => ({
        latitude: parseFloat(rota.latitude.replace(/,/g, ".")),
        longitude: parseFloat(rota.longitude.replace(/,/g, ".")),
      }));
  }, [todasRotasAmanha]);

  const todasRotasVisitadas = useMemo(() => {
    if (filters.periodo === "hoje") {
      const dataAtual = new Date().toLocaleDateString("pt-BR");
      return todasRotasHoje
        .filter((rota) => rota.dataLabel === dataAtual)
        .every((rota) => rota?.visita?.status === 1 && rota?.visita?.dataLabel === dataAtual);
    } else {
      return false;
    }
  }, [todasRotasHoje]);

  useEffect(() => {
    if (filters.periodo) {
      if (filters.periodo === "hoje") setDays([new Date().toLocaleDateString()]);
      else if (filters.periodo === "amanha") {
        const day = new Date();
        day.setDate(day.getDate() + 1);
        setDays([day.toLocaleDateString()]);
      } else if (filters.periodo === "essa-semana") setDays(semanaVigente());
      else if (filters.periodo === "esse-mes") setDays(todasAsDatasDoMes());
      else setDays(filters.periodo);
    }
  }, [filters]);

  const totalRenderizado = useMemo(() => {
    return (rotas || []).length;
  }, [rotas]);

  const [coordenadas, setCoordenadas] = useState(null);
  useEffect(() => {
    const encontrarCoordenadas = () => {
      for (const rota of rotas) {
        if (
          typeof rota.latitude === "string" &&
          typeof rota.longitude === "string" &&
          rota.latitude.length > 2 &&
          rota.longitude.length > 2
        ) {
          return { latitude: rota.latitude, longitude: rota.longitude };
        }
      }
      return null;
    };

    const resultado = encontrarCoordenadas();
    setCoordenadas(resultado);
  }, [rotas]);

  return {
    rotasCoordenadas,
    rotasCoordenadasAmanha,
    coordenadas,
    rotas,
    totalRenderizado,
    total,
    todasRotasVisitadas,
  };
};

export default useRota;
